import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  InputLabel,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import { useLanguage } from "../contexts/LanguageContext";
import { formatMessage } from "../locales";
import { API_URL } from "../config";

function Debate() {
  const { messages } = useLanguage();
  const [yourFaith, setYourFaith] = useState("");
  const [opponentFaith, setOpponentFaith] = useState("");
  const [debateTone, setDebateTone] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDebateStarted, setIsDebateStarted] = useState(false);
  const messagesEndRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [chatId, setChatId] = useState(null);

  const faiths = [
    "christian",
    "muslim",
    "jewish",
    "buddhist",
    "hindu",
    "atheist",
    "agnostic",
    "secular",
  ];

  const tones = [
    {
      value: "logical",
      label: formatMessage(messages, "debate.tones.logical"),
    },
    {
      value: "socratic",
      label: formatMessage(messages, "debate.tones.socratic"),
    },
    {
      value: "direct",
      label: formatMessage(messages, "debate.tones.direct"),
    },
    {
      value: "emotional",
      label: formatMessage(messages, "debate.tones.emotional"),
    },
    {
      value: "diplomatic",
      label: formatMessage(messages, "debate.tones.diplomatic"),
    },
    {
      value: "narrative",
      label: formatMessage(messages, "debate.tones.narrative"),
    },
    {
      value: "humorous",
      label: formatMessage(messages, "debate.tones.humorous"),
    },
    {
      value: "critical",
      label: formatMessage(messages, "debate.tones.critical"),
    },
    {
      value: "authoritative",
      label: formatMessage(messages, "debate.tones.authoritative"),
    },
    {
      value: "plainspoken",
      label: formatMessage(messages, "debate.tones.plainspoken"),
    },
  ];

  // Function to get random item from array
  const getRandomItem = (array) => {
    return array[Math.floor(Math.random() * array.length)];
  };

  // Set random values on mount
  useEffect(() => {
    const randomYourFaith = getRandomItem(faiths);
    let randomOpponentFaith;
    // Keep selecting until we get a different faith for the opponent
    do {
      randomOpponentFaith = getRandomItem(faiths);
    } while (randomOpponentFaith === randomYourFaith);

    const randomTone = getRandomItem(tones);

    setYourFaith(randomYourFaith);
    setOpponentFaith(randomOpponentFaith);
    setDebateTone(randomTone.value);
  }, []);

  const letsGoSuggestion =
    formatMessage(messages, "debate.chat.letsGo") || "Let's go!";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleStartDebate = async () => {
    setIsDebateStarted(true);
    setChatMessages([]);
    setInput("");
    setIsLoading(true);
    try {
      const selectedTone = tones.find((t) => t.value === debateTone);
      const response = await fetch(`${API_URL}/api/debate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          yourFaith,
          opponentFaith,
          debateTone: selectedTone,
          conversationHistory: [],
          chatId,
        }),
      });

      const data = await response.json();
      if (!chatId) {
        setChatId(data.chatId);
      }
      setChatMessages([{ role: "assistant", content: data.response }]);
    } catch (error) {
      setChatMessages([
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !yourFaith || !opponentFaith || !debateTone) return;
    const userMessage = input.trim();
    setInput("");

    if (!isDebateStarted) {
      setIsDebateStarted(true);
      setChatMessages([]);
    }

    const newMessage = { role: "user", content: userMessage };
    setChatMessages((prev) =>
      isDebateStarted ? [...prev, newMessage] : [newMessage]
    );
    setIsLoading(true);
    setShouldScroll(true);

    try {
      const selectedTone = tones.find((t) => t.value === debateTone);
      const response = await fetch(`${API_URL}/api/debate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          yourFaith,
          opponentFaith,
          debateTone: selectedTone,
          conversationHistory: isDebateStarted
            ? chatMessages.concat(newMessage)
            : [newMessage],
          chatId,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = { role: "assistant", content: "" };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter(Boolean);

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));
            if (data.done) {
              if (!chatId && data.chatId) {
                setChatId(data.chatId);
              }
              break;
            }
            if (data.content) {
              assistantMessage.content += data.content;
              setChatMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === "assistant") {
                  lastMessage.content = assistantMessage.content;
                } else {
                  newMessages.push({ ...assistantMessage });
                }
                return newMessages;
              });
            }
          }
        }
      }
    } catch (error) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setInput("");
    await handleSendSuggestion(suggestion);
  };

  const handleSendSuggestion = async (suggestion) => {
    if (!suggestion.trim() || isLoading) return;
    setIsDebateStarted(true);
    const userMessage = suggestion.trim();
    const newMessage = { role: "user", content: userMessage };
    setChatMessages([newMessage]);
    setIsLoading(true);
    setShouldScroll(true);

    try {
      const selectedTone = tones.find((t) => t.value === debateTone);
      const response = await fetch(`${API_URL}/api/debate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          yourFaith,
          opponentFaith,
          debateTone: selectedTone,
          conversationHistory: [newMessage],
          chatId,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = { role: "assistant", content: "" };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter(Boolean);

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));
            if (data.done) {
              if (!chatId && data.chatId) {
                setChatId(data.chatId);
              }
              break;
            }
            if (data.content) {
              assistantMessage.content += data.content;
              setChatMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === "assistant") {
                  lastMessage.content = assistantMessage.content;
                } else {
                  newMessages.push({ ...assistantMessage });
                }
                return newMessages;
              });
            }
          }
        }
      }
    } catch (error) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      shouldScroll &&
      chatMessages.length > 0 &&
      chatMessages[chatMessages.length - 1].role === "user"
    ) {
      scrollToBottom();
      setShouldScroll(false);
    }
  }, [chatMessages, shouldScroll]);

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          height: "calc(100vh - 64px)",
          overflowY: "hidden",
          pt: { xs: 2, sm: 3 },
        }}
      >
        {/* Selectors */}
        {!isDebateStarted && (
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "flex-start",
                overflowX: "auto",
                pt: 1,
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "rgba(0,0,0,0.3)",
                },
              }}
            >
              <FormControl sx={{ minWidth: 120, flexShrink: 0 }}>
                <InputLabel>
                  {formatMessage(messages, "debate.selectors.you")}
                </InputLabel>
                <Select
                  value={yourFaith}
                  onChange={(e) => setYourFaith(e.target.value)}
                  label={formatMessage(messages, "debate.selectors.you")}
                  size="small"
                >
                  {faiths.map((faith) => (
                    <MenuItem key={faith} value={faith}>
                      {formatMessage(messages, `debate.faiths.${faith}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120, flexShrink: 0 }}>
                <InputLabel>
                  {formatMessage(messages, "debate.selectors.opponent")}
                </InputLabel>
                <Select
                  value={opponentFaith}
                  onChange={(e) => setOpponentFaith(e.target.value)}
                  label={formatMessage(messages, "debate.selectors.opponent")}
                  size="small"
                >
                  {faiths.map((faith) => (
                    <MenuItem key={faith} value={faith}>
                      {formatMessage(messages, `debate.faiths.${faith}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200, flexShrink: 0 }}>
                <InputLabel id="debate-tone-label">
                  {formatMessage(messages, "debate.selectors.tone")}
                </InputLabel>
                <Select
                  labelId="debate-tone-label"
                  value={debateTone}
                  onChange={(e) => setDebateTone(e.target.value)}
                  label={formatMessage(messages, "debate.selectors.tone")}
                  size="small"
                >
                  {tones.map((tone) => (
                    <MenuItem key={tone.value} value={tone.value}>
                      {tone.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Chat Area */}
        <Box
          sx={{
            height: "calc(100vh - 64px - 80px)",
            overflowY: "auto",
            px: 2,
          }}
        >
          {!isDebateStarted && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                textAlign: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: "600px" }}
              >
                {formatMessage(messages, "debate.welcome")}
              </Typography>
              <Paper
                elevation={2}
                sx={{
                  display: "inline-flex",
                  p: 1.2,
                  px: 2,
                  cursor:
                    !yourFaith || !opponentFaith || !debateTone || isLoading
                      ? "not-allowed"
                      : "pointer",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  opacity:
                    !yourFaith || !opponentFaith || !debateTone || isLoading
                      ? 0.5
                      : 1,
                  pointerEvents:
                    !yourFaith || !opponentFaith || !debateTone || isLoading
                      ? "none"
                      : "auto",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                }}
                onClick={() => handleSuggestionClick(letsGoSuggestion)}
              >
                <Typography variant="body2">
                  {formatMessage(messages, "debate.computerStart")}
                </Typography>
              </Paper>
            </Box>
          )}
          {isDebateStarted && (
            <>
              {chatMessages.length === 0 && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 2 }}
                >
                  {formatMessage(messages, "debate.chat.start")}
                </Typography>
              )}
              {chatMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      message.role === "user" ? "flex-end" : "flex-start",
                    mb: 2,
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: "80%",
                      bgcolor:
                        message.role === "user"
                          ? "primary.main"
                          : "background.paper",
                      color:
                        message.role === "user"
                          ? "primary.contrastText"
                          : "text.primary",
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {message.content}
                    </Typography>
                  </Paper>
                  {message.role === "assistant" && (
                    <Box sx={{ mt: 0.5, ml: 1 }}>
                      <a
                        href="/read"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "none",
                          fontSize: "0.9em",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <SearchIcon sx={{ fontSize: "1em" }} />
                        {formatMessage(messages, "debate.research") ||
                          "Research your answer"}
                      </a>
                    </Box>
                  )}
                </Box>
              ))}
              {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input Area */}
        <Container
          maxWidth="lg"
          sx={{
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Box sx={{ p: 2, bgcolor: "background.default" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  !isDebateStarted
                    ? formatMessage(messages, "debate.chat.startTyping") ||
                      "Type anything to start the debate..."
                    : formatMessage(messages, "debate.chat.placeholder")
                }
                variant="outlined"
                size="small"
                disabled={!yourFaith || !opponentFaith || !debateTone}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={
                  isLoading ||
                  !input.trim() ||
                  !yourFaith ||
                  !opponentFaith ||
                  !debateTone
                }
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </Container>
  );
}

export default Debate;
